import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getConsentChoice,
  hasAnalyticsConsent,
  setAnalyticsConsent,
} from '../utils/analytics';
import '../styles/CookieConsentBanner.css';

const TEXTS = {
  en: {
    title: 'Your privacy settings',
    description:
      'Mapapet uses essential cookies for the website to work. With your permission, we also use Google Analytics to understand which pages are useful and improve the service.',
    acceptAll: 'Accept analytics',
    necessaryOnly: 'Essential only',
    settings: 'Settings',
    save: 'Save choice',
    analyticsTitle: 'Analytics',
    analyticsDescription:
      'Anonymous usage statistics through Google Analytics.',
    necessaryTitle: 'Essential cookies',
    necessaryDescription:
      'Required for sign-in, security, language and basic website functions.',
    alwaysOn: 'Always on',
    privacy: 'Privacy policy',
    reopen: 'Cookie settings',
    close: 'Close',
  },
  ru: {
    title: 'Настройки конфиденциальности',
    description:
      'Mapapet использует необходимые cookies для работы сайта. С вашего разрешения мы также используем Google Analytics, чтобы понимать, какие страницы полезны, и улучшать сервис.',
    acceptAll: 'Принять аналитику',
    necessaryOnly: 'Только необходимые',
    settings: 'Настроить',
    save: 'Сохранить выбор',
    analyticsTitle: 'Аналитика',
    analyticsDescription:
      'Анонимная статистика использования через Google Analytics.',
    necessaryTitle: 'Необходимые cookies',
    necessaryDescription:
      'Нужны для входа, безопасности, языка и основных функций сайта.',
    alwaysOn: 'Всегда включены',
    privacy: 'Политика конфиденциальности',
    reopen: 'Настройки cookies',
    close: 'Закрыть',
  },
  de: {
    title: 'Datenschutzeinstellungen',
    description:
      'Mapapet verwendet notwendige Cookies für den Betrieb der Website. Mit Ihrer Zustimmung nutzen wir außerdem Google Analytics, um den Dienst zu verbessern.',
    acceptAll: 'Analyse akzeptieren',
    necessaryOnly: 'Nur notwendige',
    settings: 'Einstellungen',
    save: 'Auswahl speichern',
    analyticsTitle: 'Analyse',
    analyticsDescription:
      'Anonyme Nutzungsstatistiken über Google Analytics.',
    necessaryTitle: 'Notwendige Cookies',
    necessaryDescription:
      'Erforderlich für Anmeldung, Sicherheit, Sprache und Grundfunktionen.',
    alwaysOn: 'Immer aktiv',
    privacy: 'Datenschutzerklärung',
    reopen: 'Cookie-Einstellungen',
    close: 'Schließen',
  },
  uk: {
    title: 'Налаштування конфіденційності',
    description:
      'Mapapet використовує необхідні cookies для роботи сайту. З вашого дозволу ми також використовуємо Google Analytics, щоб покращувати сервіс.',
    acceptAll: 'Прийняти аналітику',
    necessaryOnly: 'Лише необхідні',
    settings: 'Налаштувати',
    save: 'Зберегти вибір',
    analyticsTitle: 'Аналітика',
    analyticsDescription:
      'Анонімна статистика використання через Google Analytics.',
    necessaryTitle: 'Необхідні cookies',
    necessaryDescription:
      'Потрібні для входу, безпеки, мови та основних функцій.',
    alwaysOn: 'Завжди увімкнені',
    privacy: 'Політика конфіденційності',
    reopen: 'Налаштування cookies',
    close: 'Закрити',
  },
  es: {
    title: 'Configuración de privacidad',
    description:
      'Mapapet usa cookies esenciales para funcionar. Con tu permiso, también usamos Google Analytics para mejorar el servicio.',
    acceptAll: 'Aceptar analítica',
    necessaryOnly: 'Solo esenciales',
    settings: 'Configurar',
    save: 'Guardar elección',
    analyticsTitle: 'Analítica',
    analyticsDescription:
      'Estadísticas anónimas mediante Google Analytics.',
    necessaryTitle: 'Cookies esenciales',
    necessaryDescription:
      'Necesarias para el acceso, la seguridad, el idioma y las funciones básicas.',
    alwaysOn: 'Siempre activas',
    privacy: 'Política de privacidad',
    reopen: 'Configurar cookies',
    close: 'Cerrar',
  },
  fr: {
    title: 'Paramètres de confidentialité',
    description:
      'Mapapet utilise des cookies essentiels au fonctionnement du site. Avec votre accord, Google Analytics nous aide aussi à améliorer le service.',
    acceptAll: 'Accepter les statistiques',
    necessaryOnly: 'Essentiels uniquement',
    settings: 'Paramètres',
    save: 'Enregistrer',
    analyticsTitle: 'Statistiques',
    analyticsDescription:
      'Statistiques d’utilisation anonymes via Google Analytics.',
    necessaryTitle: 'Cookies essentiels',
    necessaryDescription:
      'Nécessaires à la connexion, à la sécurité, à la langue et aux fonctions de base.',
    alwaysOn: 'Toujours actifs',
    privacy: 'Politique de confidentialité',
    reopen: 'Paramètres des cookies',
    close: 'Fermer',
  },
  pt: {
    title: 'Definições de privacidade',
    description:
      'A Mapapet usa cookies essenciais para funcionar. Com a sua permissão, também usamos o Google Analytics para melhorar o serviço.',
    acceptAll: 'Aceitar análise',
    necessaryOnly: 'Apenas essenciais',
    settings: 'Definições',
    save: 'Guardar escolha',
    analyticsTitle: 'Análise',
    analyticsDescription:
      'Estatísticas anónimas através do Google Analytics.',
    necessaryTitle: 'Cookies essenciais',
    necessaryDescription:
      'Necessários para início de sessão, segurança, idioma e funções básicas.',
    alwaysOn: 'Sempre ativos',
    privacy: 'Política de privacidade',
    reopen: 'Definições de cookies',
    close: 'Fechar',
  },
  it: {
    title: 'Impostazioni sulla privacy',
    description:
      'Mapapet usa cookie essenziali per funzionare. Con il tuo consenso usiamo anche Google Analytics per migliorare il servizio.',
    acceptAll: 'Accetta analisi',
    necessaryOnly: 'Solo essenziali',
    settings: 'Impostazioni',
    save: 'Salva scelta',
    analyticsTitle: 'Analisi',
    analyticsDescription:
      'Statistiche anonime tramite Google Analytics.',
    necessaryTitle: 'Cookie essenziali',
    necessaryDescription:
      'Necessari per accesso, sicurezza, lingua e funzioni di base.',
    alwaysOn: 'Sempre attivi',
    privacy: 'Informativa sulla privacy',
    reopen: 'Impostazioni cookie',
    close: 'Chiudi',
  },
  pl: {
    title: 'Ustawienia prywatności',
    description:
      'Mapapet używa niezbędnych plików cookie. Za zgodą używamy też Google Analytics, aby ulepszać serwis.',
    acceptAll: 'Akceptuj analitykę',
    necessaryOnly: 'Tylko niezbędne',
    settings: 'Ustawienia',
    save: 'Zapisz wybór',
    analyticsTitle: 'Analityka',
    analyticsDescription:
      'Anonimowe statystyki przez Google Analytics.',
    necessaryTitle: 'Niezbędne pliki cookie',
    necessaryDescription:
      'Wymagane do logowania, bezpieczeństwa, języka i podstawowych funkcji.',
    alwaysOn: 'Zawsze aktywne',
    privacy: 'Polityka prywatności',
    reopen: 'Ustawienia cookie',
    close: 'Zamknij',
  },
  tr: {
    title: 'Gizlilik ayarları',
    description:
      'Mapapet, sitenin çalışması için gerekli çerezleri kullanır. İzninizle hizmeti geliştirmek için Google Analytics de kullanırız.',
    acceptAll: 'Analitiği kabul et',
    necessaryOnly: 'Yalnızca gerekli',
    settings: 'Ayarlar',
    save: 'Seçimi kaydet',
    analyticsTitle: 'Analitik',
    analyticsDescription:
      'Google Analytics üzerinden anonim kullanım istatistikleri.',
    necessaryTitle: 'Gerekli çerezler',
    necessaryDescription:
      'Giriş, güvenlik, dil ve temel işlevler için gereklidir.',
    alwaysOn: 'Her zaman açık',
    privacy: 'Gizlilik politikası',
    reopen: 'Çerez ayarları',
    close: 'Kapat',
  },
  id: {
    title: 'Pengaturan privasi',
    description:
      'Mapapet menggunakan cookie penting agar situs berfungsi. Dengan izin Anda, kami juga menggunakan Google Analytics untuk meningkatkan layanan.',
    acceptAll: 'Terima analitik',
    necessaryOnly: 'Hanya penting',
    settings: 'Pengaturan',
    save: 'Simpan pilihan',
    analyticsTitle: 'Analitik',
    analyticsDescription:
      'Statistik penggunaan anonim melalui Google Analytics.',
    necessaryTitle: 'Cookie penting',
    necessaryDescription:
      'Diperlukan untuk masuk, keamanan, bahasa, dan fungsi dasar.',
    alwaysOn: 'Selalu aktif',
    privacy: 'Kebijakan privasi',
    reopen: 'Pengaturan cookie',
    close: 'Tutup',
  },
  hi: {
    title: 'गोपनीयता सेटिंग्स',
    description:
      'Mapapet वेबसाइट चलाने के लिए आवश्यक कुकीज़ का उपयोग करता है। आपकी अनुमति से हम सेवा सुधारने के लिए Google Analytics का भी उपयोग करते हैं।',
    acceptAll: 'Analytics स्वीकार करें',
    necessaryOnly: 'केवल आवश्यक',
    settings: 'सेटिंग्स',
    save: 'चयन सहेजें',
    analyticsTitle: 'Analytics',
    analyticsDescription:
      'Google Analytics के माध्यम से गुमनाम उपयोग आँकड़े।',
    necessaryTitle: 'आवश्यक कुकीज़',
    necessaryDescription:
      'लॉगिन, सुरक्षा, भाषा और मूल कार्यों के लिए आवश्यक।',
    alwaysOn: 'हमेशा चालू',
    privacy: 'गोपनीयता नीति',
    reopen: 'कुकी सेटिंग्स',
    close: 'बंद करें',
  },
  zh: {
    title: '隐私设置',
    description:
      'Mapapet 使用必要 Cookie 以保证网站运行。经您同意，我们也会使用 Google Analytics 改进服务。',
    acceptAll: '接受分析',
    necessaryOnly: '仅必要 Cookie',
    settings: '设置',
    save: '保存选择',
    analyticsTitle: '分析',
    analyticsDescription:
      '通过 Google Analytics 收集匿名使用统计。',
    necessaryTitle: '必要 Cookie',
    necessaryDescription:
      '用于登录、安全、语言和基本功能。',
    alwaysOn: '始终开启',
    privacy: '隐私政策',
    reopen: 'Cookie 设置',
    close: '关闭',
  },
  ja: {
    title: 'プライバシー設定',
    description:
      'Mapapet はサイト運営に必要な Cookie を使用します。同意をいただいた場合、サービス改善のため Google Analytics も使用します。',
    acceptAll: '分析を許可',
    necessaryOnly: '必要なもののみ',
    settings: '設定',
    save: '選択を保存',
    analyticsTitle: '分析',
    analyticsDescription:
      'Google Analytics による匿名の利用統計。',
    necessaryTitle: '必須 Cookie',
    necessaryDescription:
      'ログイン、セキュリティ、言語、基本機能に必要です。',
    alwaysOn: '常に有効',
    privacy: 'プライバシーポリシー',
    reopen: 'Cookie 設定',
    close: '閉じる',
  },
  ko: {
    title: '개인정보 설정',
    description:
      'Mapapet은 사이트 작동에 필요한 쿠키를 사용합니다. 동의하면 서비스 개선을 위해 Google Analytics도 사용합니다.',
    acceptAll: '분석 허용',
    necessaryOnly: '필수만',
    settings: '설정',
    save: '선택 저장',
    analyticsTitle: '분석',
    analyticsDescription:
      'Google Analytics를 통한 익명 사용 통계입니다.',
    necessaryTitle: '필수 쿠키',
    necessaryDescription:
      '로그인, 보안, 언어 및 기본 기능에 필요합니다.',
    alwaysOn: '항상 사용',
    privacy: '개인정보 처리방침',
    reopen: '쿠키 설정',
    close: '닫기',
  },
  ar: {
    title: 'إعدادات الخصوصية',
    description:
      'تستخدم Mapapet ملفات تعريف الارتباط الضرورية لتشغيل الموقع. وبموافقتك نستخدم Google Analytics لتحسين الخدمة.',
    acceptAll: 'قبول التحليلات',
    necessaryOnly: 'الضرورية فقط',
    settings: 'الإعدادات',
    save: 'حفظ الاختيار',
    analyticsTitle: 'التحليلات',
    analyticsDescription:
      'إحصاءات استخدام مجهولة عبر Google Analytics.',
    necessaryTitle: 'ملفات الارتباط الضرورية',
    necessaryDescription:
      'مطلوبة لتسجيل الدخول والأمان واللغة والوظائف الأساسية.',
    alwaysOn: 'مفعلة دائمًا',
    privacy: 'سياسة الخصوصية',
    reopen: 'إعدادات ملفات الارتباط',
    close: 'إغلاق',
  },
};

const normalizeLanguage = (language) => {
  const shortLanguage = String(language || 'en')
    .toLowerCase()
    .split('-')[0];

  return TEXTS[shortLanguage] ? shortLanguage : 'en';
};

const CookieConsentBanner = () => {
  const { i18n } = useTranslation();

  const [visible, setVisible] = useState(() => !getConsentChoice());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(() =>
    hasAnalyticsConsent()
  );

  const language = normalizeLanguage(
    i18n.resolvedLanguage || i18n.language
  );

  const text = useMemo(() => TEXTS[language] || TEXTS.en, [language]);

  useEffect(() => {
    const openSettings = () => {
      setAnalyticsEnabled(hasAnalyticsConsent());
      setSettingsOpen(true);
      setVisible(true);
    };

    window.addEventListener(
      'mapapet-open-cookie-settings',
      openSettings
    );

    return () => {
      window.removeEventListener(
        'mapapet-open-cookie-settings',
        openSettings
      );
    };
  }, []);

  const closeAfterChoice = () => {
    setVisible(false);
    setSettingsOpen(false);
  };

  const acceptAnalytics = () => {
    setAnalyticsConsent(true);
    setAnalyticsEnabled(true);
    closeAfterChoice();
  };

  const acceptNecessaryOnly = () => {
    setAnalyticsConsent(false);
    setAnalyticsEnabled(false);
    closeAfterChoice();
  };

  const saveSettings = () => {
    setAnalyticsConsent(analyticsEnabled);
    closeAfterChoice();
  };

  const closeWithoutSaving = () => {
    setAnalyticsEnabled(hasAnalyticsConsent());
    setSettingsOpen(false);
    setVisible(false);
  };

  if (!visible) {
    return (
      <button
        type="button"
        className="cookie-consent-reopen"
        onClick={() => {
          setAnalyticsEnabled(hasAnalyticsConsent());
          setSettingsOpen(true);
          setVisible(true);
        }}
        aria-label={text.reopen}
        title={text.reopen}
      >
        🍪
      </button>
    );
  }

  return (
    <div
      className="cookie-consent-shell"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
    >
      <div className="cookie-consent-card">
        <button
          type="button"
          className="cookie-consent-close"
          onClick={closeWithoutSaving}
          aria-label={text.close}
          title={text.close}
        >
          ×
        </button>

        <div className="cookie-consent-copy">
          <h2 id="cookie-consent-title">{text.title}</h2>
          <p>{text.description}</p>

          <a href="/privacy" className="cookie-consent-privacy-link">
            {text.privacy}
          </a>
        </div>

        {settingsOpen && (
          <div className="cookie-consent-settings">
            <div className="cookie-consent-option">
              <div>
                <strong>{text.necessaryTitle}</strong>
                <span>{text.necessaryDescription}</span>
              </div>

              <span className="cookie-consent-always-on">
                {text.alwaysOn}
              </span>
            </div>

            <label className="cookie-consent-option">
              <div>
                <strong>{text.analyticsTitle}</strong>
                <span>{text.analyticsDescription}</span>
              </div>

              <input
                type="checkbox"
                checked={analyticsEnabled}
                onChange={(event) =>
                  setAnalyticsEnabled(event.target.checked)
                }
              />
            </label>
          </div>
        )}

        <div className="cookie-consent-actions">
          {!settingsOpen ? (
            <>
              <button
                type="button"
                className="cookie-consent-button cookie-consent-button--primary"
                onClick={acceptAnalytics}
              >
                {text.acceptAll}
              </button>

              <button
                type="button"
                className="cookie-consent-button"
                onClick={acceptNecessaryOnly}
              >
                {text.necessaryOnly}
              </button>

              <button
                type="button"
                className="cookie-consent-button cookie-consent-button--link"
                onClick={() => setSettingsOpen(true)}
              >
                {text.settings}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="cookie-consent-button cookie-consent-button--primary"
              onClick={saveSettings}
            >
              {text.save}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
